import React, { useState, useEffect } from "react";
import {
  Button,
  Container,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  InputAdornment,
  Chip,
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  FormControlLabel,
  Switch,
  Alert,
  Divider,
  Paper,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Badge
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  IntegrationInstructions as IntegrationIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  ContentCopy as CopyIcon,
  Settings as SettingsIcon,
  AddCircleOutline as AddFieldIcon
} from "@mui/icons-material";
import Header from "../components/Header";
import ObjectCard from "../components/Common/ObjectCard";
import axios from "axios";

// Templates de integración predefinidos
const INTEGRATION_TEMPLATES = {
  JIRA: {
    name: 'Jira',
    icon: '🎯',
    description: 'Integración con Atlassian Jira para gestión de proyectos y seguimiento de issues',
    requiredFields: [
      { key: 'api_url', label: 'API URL', type: 'url', placeholder: 'https://your-domain.atlassian.net', required: true },
      { key: 'email', label: 'Email', type: 'email', placeholder: 'user@example.com', required: true },
      { key: 'api_token', label: 'API Token', type: 'password', placeholder: 'Your Jira API token', required: true },
      { key: 'project_key', label: 'Project Key', type: 'text', placeholder: 'PROJ', required: false }
    ]
  },
  SALESFORCE: {
    name: 'Salesforce',
    icon: '☁️',
    description: 'Conecta con Salesforce CRM para sincronizar clientes, oportunidades y casos',
    requiredFields: [
      { key: 'instance_url', label: 'Instance URL', type: 'url', placeholder: 'https://your-instance.salesforce.com', required: true },
      { key: 'client_id', label: 'Client ID', type: 'text', placeholder: 'Consumer Key from Connected App', required: true },
      { key: 'client_secret', label: 'Client Secret', type: 'password', placeholder: 'Consumer Secret', required: true },
      { key: 'username', label: 'Username', type: 'email', placeholder: 'user@example.com', required: true },
      { key: 'password', label: 'Password + Security Token', type: 'password', placeholder: 'PasswordToken', required: true }
    ]
  },
  SLACK: {
    name: 'Slack',
    icon: '💬',
    description: 'Envía notificaciones y mensajes a canales de Slack',
    requiredFields: [
      { key: 'webhook_url', label: 'Webhook URL', type: 'url', placeholder: 'https://hooks.slack.com/services/...', required: true },
      { key: 'channel', label: 'Default Channel', type: 'text', placeholder: '#general', required: false },
      { key: 'bot_name', label: 'Bot Name', type: 'text', placeholder: 'Integration Bot', required: false }
    ]
  },
  ODOO: {
    name: 'Odoo',
    icon: '📊',
    description: 'Integración con Odoo ERP para gestión empresarial completa',
    requiredFields: [
      { key: 'url', label: 'Odoo URL', type: 'url', placeholder: 'https://your-company.odoo.com', required: true },
      { key: 'database', label: 'Database', type: 'text', placeholder: 'your_database', required: true },
      { key: 'username', label: 'Username', type: 'email', placeholder: 'admin@example.com', required: true },
      { key: 'api_key', label: 'API Key', type: 'password', placeholder: 'Your Odoo API key', required: true }
    ]
  },
  GITHUB: {
    name: 'GitHub',
    icon: '🐙',
    description: 'Conecta con repositorios de GitHub para gestión de código y issues',
    requiredFields: [
      { key: 'api_url', label: 'API URL', type: 'url', placeholder: 'https://api.github.com', required: true },
      { key: 'access_token', label: 'Personal Access Token', type: 'password', placeholder: 'ghp_xxxxxxxxxxxx', required: true },
      { key: 'organization', label: 'Organization', type: 'text', placeholder: 'your-org', required: false },
      { key: 'repository', label: 'Default Repository', type: 'text', placeholder: 'repo-name', required: false }
    ]
  },
  CUSTOM: {
    name: 'Personalizada',
    icon: '⚙️',
    description: 'Configuración personalizada para integraciones específicas',
    requiredFields: []
  }
};

export default function IntegrationManager() {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editIntegration, setEditIntegration] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedIntegrationMenu, setSelectedIntegrationMenu] = useState(null);
  const [showPasswords, setShowPasswords] = useState({});
  const [testResult, setTestResult] = useState(null);
  const [showTestAlert, setShowTestAlert] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  // Estado para campos personalizados
  const [customFields, setCustomFields] = useState([]);
  const [newFieldKey, setNewFieldKey] = useState('');
  const [newFieldValue, setNewFieldValue] = useState('');
  const [newFieldType, setNewFieldType] = useState('text');

  // Valores por defecto para una nueva integración
  const getDefaultIntegration = () => ({
    title: "",
    description: "",
    type: "CUSTOM",
    icon: "⚙️",
    enabled: true,
    configuration: [],
    lastTestDate: null,
    lastTestStatus: null,
    statistics: {
      successfulCalls: 0,
      failedCalls: 0,
      lastUsed: null
    }
  });

  // Generar UUID
  const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  // Obtener icono para la integración
  const getIntegrationIcon = (integration) => {
    if (integration.icon) return integration.icon;
    const template = INTEGRATION_TEMPLATES[integration.type];
    return template?.icon || '⚙️';
  };

  // Obtener tags de la integración
  const getIntegrationTags = (integration) => {
    const tags = [];
    
    // Tipo de integración
    if (integration.type) {
      tags.push(integration.type);
    }
    
    // Estado de la última prueba
    if (integration.lastTestStatus === 'success') {
      tags.push('✓ Verificado');
    } else if (integration.lastTestStatus === 'failed') {
      tags.push('✗ Error');
    }
    
    // Cantidad de configuraciones
    if (integration.configuration && integration.configuration.length > 0) {
      tags.push(`${integration.configuration.length} params`);
    }
    
    return tags;
  };

  // Traer datos del backend
  const fetchIntegrations = async () => {
    setLoading(true);
    try {
      const res = await axios.get("http://localhost:4000/integrations");
      setIntegrations(res.data);
    } catch (err) {
      console.error("Error fetching integrations:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIntegrations();
  }, []);

  // Filtrar integraciones
  const filteredIntegrations = integrations.filter(integration => {
    const matchSearch = 
      integration.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      integration.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      integration.type?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchType = filterType === "all" || integration.type === filterType;
    return matchSearch && matchType;
  });

  // Handler para abrir el diálogo de edición
  const handleOpenEdit = (integrationId) => {
    const integrationToEdit = integrations.find(i => i.id === integrationId);
    if (integrationToEdit) {
      setEditIntegration({
        ...integrationToEdit,
        configuration: integrationToEdit.configuration || []
      });
      // Si es una integración existente, cargar sus campos personalizados
      if (integrationToEdit.configuration) {
        setCustomFields(integrationToEdit.configuration);
      }
      setOpenDialog(true);
    }
  };

  // Handler para crear nueva integración
  const handleCreateNew = () => {
    const newIntegration = getDefaultIntegration();
    setEditIntegration(newIntegration);
    setCustomFields([]);
    setOpenDialog(true);
  };

  // Handler cuando cambia el tipo de integración
  const handleTypeChange = (newType) => {
    const template = INTEGRATION_TEMPLATES[newType];
    if (template) {
      // Crear configuración inicial basada en el template
      const initialConfig = template.requiredFields.map(field => ({
        key: field.key,
        value: '',
        type: field.type || 'text',
        label: field.label,
        required: field.required
      }));
      
      setEditIntegration({
        ...editIntegration,
        type: newType,
        icon: template.icon,
        description: editIntegration.description || template.description
      });
      setCustomFields(initialConfig);
    }
  };

  // Handler para añadir campo personalizado
  const handleAddCustomField = () => {
    if (newFieldKey && newFieldValue) {
      const newField = {
        key: newFieldKey,
        value: newFieldValue,
        type: newFieldType,
        label: newFieldKey.charAt(0).toUpperCase() + newFieldKey.slice(1).replace(/_/g, ' '),
        required: false
      };
      setCustomFields([...customFields, newField]);
      setNewFieldKey('');
      setNewFieldValue('');
      setNewFieldType('text');
    }
  };

  // Handler para eliminar campo personalizado
  const handleRemoveCustomField = (index) => {
    setCustomFields(customFields.filter((_, i) => i !== index));
  };

  // Handler para actualizar valor de campo
  const handleFieldValueChange = (index, newValue) => {
    const updatedFields = [...customFields];
    updatedFields[index].value = newValue;
    setCustomFields(updatedFields);
  };

  // Handler para toggle de visibilidad de password
  const handleTogglePasswordVisibility = (fieldKey) => {
    setShowPasswords(prev => ({
      ...prev,
      [fieldKey]: !prev[fieldKey]
    }));
  };

  // Handler para copiar valor de campo
  const handleCopyFieldValue = (value, fieldKey) => {
    navigator.clipboard.writeText(value);
    setCopiedField(fieldKey);
    setTimeout(() => setCopiedField(null), 2000);
  };

  // Handler para probar integración
  const handleTestIntegration = async (integration) => {
    setShowTestAlert(true);
    setTestResult({ loading: true, integration: integration.title });
    
    try {
      // Simular prueba de integración
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const success = Math.random() > 0.3; // 70% de éxito simulado
      
      setTestResult({
        success,
        message: success 
          ? `Conexión exitosa con ${integration.title}` 
          : `Error al conectar con ${integration.title}: Credenciales inválidas`,
        integration: integration.title
      });
      
      // Actualizar estado de la integración
      const updatedIntegration = {
        ...integration,
        lastTestDate: new Date().toISOString(),
        lastTestStatus: success ? 'success' : 'failed',
        statistics: {
          ...integration.statistics,
          [success ? 'successfulCalls' : 'failedCalls']: 
            (integration.statistics?.[success ? 'successfulCalls' : 'failedCalls'] || 0) + 1
        },
        updatedAt: new Date().toISOString()
      };
      
      await axios.put(`http://localhost:4000/integrations/${integration.id}`, updatedIntegration);
      fetchIntegrations();
    } catch (err) {
      setTestResult({
        success: false,
        message: `Error al probar ${integration.title}`,
        integration: integration.title
      });
    }
    
    setTimeout(() => setShowTestAlert(false), 5000);
  };

  // Handler para guardar cambios
  const handleSave = async () => {
    try {
      const now = new Date().toISOString();
      
      const integrationData = {
        ...editIntegration,
        configuration: customFields,
        icon: INTEGRATION_TEMPLATES[editIntegration.type]?.icon || editIntegration.icon
      };

      if (editIntegration.id) {
        // Actualizar integración existente
        const updatedIntegration = {
          ...integrationData,
          updatedAt: now
        };
        await axios.put(`http://localhost:4000/integrations/${editIntegration.id}`, updatedIntegration);
      } else {
        // Crear nueva integración
        const newIntegration = {
          ...integrationData,
          id: generateUUID(),
          createdAt: now,
          updatedAt: now,
          lastTestDate: null,
          lastTestStatus: null,
          statistics: {
            successfulCalls: 0,
            failedCalls: 0,
            lastUsed: null
          }
        };
        await axios.post("http://localhost:4000/integrations", newIntegration);
      }
      
      setOpenDialog(false);
      setEditIntegration(null);
      setCustomFields([]);
      fetchIntegrations();
    } catch (err) {
      console.error("Error saving integration:", err);
    }
  };

  // Handler para eliminar integración
  const handleDelete = async (id) => {
    if (window.confirm("¿Está seguro de que desea eliminar esta integración? Esta acción no se puede deshacer.")) {
      try {
        await axios.delete(`http://localhost:4000/integrations/${id}`);
        fetchIntegrations();
      } catch (err) {
        console.error("Error deleting integration:", err);
      }
    }
    handleCloseMenu();
  };

  // Handler para toggle de activación
  const handleToggleActive = async (integrationId, newState) => {
    const integration = integrations.find(i => i.id === integrationId);
    if (integration) {
      try {
        const updatedIntegration = { 
          ...integration, 
          enabled: newState,
          updatedAt: new Date().toISOString()
        };
        await axios.put(`http://localhost:4000/integrations/${integration.id}`, updatedIntegration);
        fetchIntegrations();
      } catch (err) {
        console.error("Error toggling integration:", err);
      }
    }
  };

  // Menu handlers
  const handleOpenMenu = (event, integration) => {
    setAnchorEl(event.currentTarget);
    setSelectedIntegrationMenu(integration);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedIntegrationMenu(null);
  };

  // Handler para cerrar diálogo
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditIntegration(null);
    setCustomFields([]);
    setNewFieldKey('');
    setNewFieldValue('');
  };

  // Obtener tipos únicos de integración
  const uniqueTypes = [...new Set(integrations.map(i => i.type).filter(Boolean))];

  return (
    <div>
      <Header />

      <Container 
        maxWidth="lg" 
        sx={{ 
          mt: 4, 
          mb: 4,
          px: { xs: 2, sm: 3 }
        }}
      >
        {/* Test Alert */}
        {showTestAlert && testResult && (
          <Alert 
            severity={testResult.loading ? "info" : testResult.success ? "success" : "error"}
            icon={testResult.loading ? <SettingsIcon className="spinning" /> : testResult.success ? <CheckIcon /> : <ErrorIcon />}
            sx={{ mb: 2 }}
          >
            {testResult.loading ? `Probando conexión con ${testResult.integration}...` : testResult.message}
          </Alert>
        )}

        {/* Header Section */}
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          justifyContent="space-between" 
          alignItems={{ xs: 'stretch', sm: 'center' }} 
          mb={3}
          spacing={2}
        >
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              <IntegrationIcon sx={{ mr: 1, verticalAlign: 'bottom' }} />
              Gestión de Integraciones
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configura y administra las conexiones con servicios externos
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3
            }}
          >
            Nueva Integración
          </Button>
        </Stack>

        {/* Filters and Search */}
        <Stack 
          direction="column" 
          spacing={2} 
          mb={3}
        >
          <TextField
            placeholder="Buscar integraciones..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            fullWidth
            sx={{ maxWidth: { sm: 500 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 1 
          }}>
            <Chip
              label="Todas"
              onClick={() => setFilterType("all")}
              color={filterType === "all" ? "primary" : "default"}
              variant={filterType === "all" ? "filled" : "outlined"}
              size="small"
            />
            {Object.keys(INTEGRATION_TEMPLATES).filter(key => key !== 'CUSTOM').map(type => (
              <Chip
                key={type}
                label={`${INTEGRATION_TEMPLATES[type].icon} ${INTEGRATION_TEMPLATES[type].name}`}
                onClick={() => setFilterType(type)}
                color={filterType === type ? "primary" : "default"}
                variant={filterType === type ? "filled" : "outlined"}
                size="small"
              />
            ))}
          </Box>
        </Stack>

        {/* Stats */}
        <Stack 
          direction="row" 
          spacing={1} 
          mb={3}
          flexWrap="wrap"
          useFlexGap
        >
          <Chip
            label={`${filteredIntegrations.length} integraciones`}
            size="small"
            color="primary"
            variant="outlined"
          />
          <Chip
            label={`${filteredIntegrations.filter(i => i.enabled).length} activas`}
            size="small"
            color="success"
            variant="outlined"
          />
          <Chip
            label={`${filteredIntegrations.filter(i => i.lastTestStatus === 'success').length} verificadas`}
            size="small"
            color="info"
            variant="outlined"
          />
        </Stack>

        {/* Cards Grid */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)'
          },
          gap: 2
        }}>
          {loading ? (
            [...Array(4)].map((_, index) => (
              <Skeleton
                key={index}
                variant="rounded"
                height={180}
                sx={{ borderRadius: 3 }}
              />
            ))
          ) : filteredIntegrations.length > 0 ? (
            filteredIntegrations.map((integration) => (
              <Box key={integration.id} position="relative">
                <ObjectCard
                  id={integration.id}
                  icon={getIntegrationIcon(integration)}
                  title={integration.title || "Sin título"}
                  description={integration.description}
                  lastUpdated={integration.updatedAt}
                  isEnabled={integration.enabled !== false}
                  tags={getIntegrationTags(integration)}
                  onClick={handleOpenEdit}
                  onToggle={handleToggleActive}
                  variant={
                    !integration.enabled ? 'default' :
                    integration.lastTestStatus === 'success' ? 'success' :
                    integration.lastTestStatus === 'failed' ? 'danger' :
                    'info'
                  }
                />

                <IconButton
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    bgcolor: 'background.paper',
                    boxShadow: 1,
                    '&:hover': { boxShadow: 2 }
                  }}
                  onClick={(e) => handleOpenMenu(e, integration)}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
            ))
          ) : (
            <Box sx={{
              gridColumn: '1 / -1',
              textAlign: 'center',
              py: 8
            }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No se encontraron integraciones
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchTerm ? "Intenta con otros términos de búsqueda" : "Crea tu primera integración"}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Context Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
        >
          <MenuItem onClick={() => {
            handleOpenEdit(selectedIntegrationMenu.id);
            handleCloseMenu();
          }}>
            Editar
          </MenuItem>
          <MenuItem onClick={() => {
            handleTestIntegration(selectedIntegrationMenu);
            handleCloseMenu();
          }}>
            Probar Conexión
          </MenuItem>
          <Divider />
          <MenuItem 
            onClick={() => handleDelete(selectedIntegrationMenu?.id)}
            sx={{ color: 'error.main' }}
          >
            Eliminar
          </MenuItem>
        </Menu>

        {/* Edit/Create Dialog */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>
            {editIntegration?.id ? `Editar: ${editIntegration.title}` : "Nueva Integración"}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3} mt={2}>
              {/* Información básica */}
              <TextField
                label="Título"
                value={editIntegration?.title || ""}
                onChange={(e) => setEditIntegration({ ...editIntegration, title: e.target.value })}
                fullWidth
                required
                helperText="Nombre descriptivo para identificar la integración"
              />

              <TextField
                label="Descripción"
                value={editIntegration?.description || ""}
                onChange={(e) => setEditIntegration({ ...editIntegration, description: e.target.value })}
                fullWidth
                multiline
                rows={2}
                inputProps={{ maxLength: 160 }}
                helperText={`${editIntegration?.description?.length || 0}/160 caracteres`}
              />

              {/* Tipo de integración */}
              <FormControl fullWidth>
                <InputLabel>Tipo de Integración</InputLabel>
                <Select
                  value={editIntegration?.type || "CUSTOM"}
                  onChange={(e) => handleTypeChange(e.target.value)}
                  label="Tipo de Integración"
                >
                  {Object.entries(INTEGRATION_TEMPLATES).map(([key, template]) => (
                    <MenuItem key={key} value={key}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <span>{template.icon}</span>
                        <span>{template.name}</span>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Configuración de campos */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Configuración
                </Typography>
                
                {/* Campos existentes */}
                {customFields.length > 0 ? (
                  <Stack spacing={2} mt={2}>
                    {customFields.map((field, index) => (
                      <Box key={`${field.key}-${index}`}>
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          <TextField
                            label={field.label || field.key}
                            type={showPasswords[field.key] ? 'text' : field.type}
                            value={field.value}
                            onChange={(e) => handleFieldValueChange(index, e.target.value)}
                            fullWidth
                            required={field.required}
                            placeholder={INTEGRATION_TEMPLATES[editIntegration?.type]?.requiredFields?.find(f => f.key === field.key)?.placeholder}
                            InputProps={{
                              endAdornment: field.type === 'password' && (
                                <InputAdornment position="end">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleTogglePasswordVisibility(field.key)}
                                  >
                                    {showPasswords[field.key] ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                  </IconButton>
                                </InputAdornment>
                              )
                            }}
                          />
                          <Tooltip title="Copiar valor">
                            <IconButton 
                              size="small" 
                              onClick={() => handleCopyFieldValue(field.value, field.key)}
                              color={copiedField === field.key ? "success" : "default"}
                            >
                              {copiedField === field.key ? <CheckIcon /> : <CopyIcon />}
                            </IconButton>
                          </Tooltip>
                          {!field.required && (
                            <Tooltip title="Eliminar campo">
                              <IconButton 
                                size="small" 
                                color="error"
                                onClick={() => handleRemoveCustomField(index)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    No hay campos configurados. Añade campos personalizados abajo.
                  </Alert>
                )}

                {/* Añadir campo personalizado */}
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Añadir Campo Personalizado
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} mt={1}>
                  <TextField
                    size="small"
                    label="Clave"
                    value={newFieldKey}
                    onChange={(e) => setNewFieldKey(e.target.value)}
                    placeholder="api_key"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    size="small"
                    label="Valor"
                    value={newFieldValue}
                    onChange={(e) => setNewFieldValue(e.target.value)}
                    placeholder="valor"
                    sx={{ flex: 1 }}
                  />
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <Select
                      value={newFieldType}
                      onChange={(e) => setNewFieldType(e.target.value)}
                    >
                      <MenuItem value="text">Texto</MenuItem>
                      <MenuItem value="password">Contraseña</MenuItem>
                      <MenuItem value="url">URL</MenuItem>
                      <MenuItem value="email">Email</MenuItem>
                      <MenuItem value="number">Número</MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    variant="outlined"
                    startIcon={<AddFieldIcon />}
                    onClick={handleAddCustomField}
                    disabled={!newFieldKey || !newFieldValue}
                    size="small"
                  >
                    Añadir
                  </Button>
                </Stack>
              </Paper>

              {/* Estado activo/inactivo */}
              <FormControlLabel
                control={
                  <Switch
                    checked={editIntegration?.enabled !== false}
                    onChange={(e) => setEditIntegration({ ...editIntegration, enabled: e.target.checked })}
                  />
                }
                label="Integración activa"
              />

              {/* Estadísticas (solo en modo edición) */}
              {editIntegration?.id && editIntegration?.statistics && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Estadísticas
                  </Typography>
                  <Stack direction="row" spacing={3} mt={1}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Llamadas Exitosas
                      </Typography>
                      <Typography variant="h6" color="success.main">
                        {editIntegration.statistics.successfulCalls || 0}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Llamadas Fallidas
                      </Typography>
                      <Typography variant="h6" color="error.main">
                        {editIntegration.statistics.failedCalls || 0}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Última Prueba
                      </Typography>
                      <Typography variant="body2">
                        {editIntegration.lastTestDate ? 
                          new Date(editIntegration.lastTestDate).toLocaleString('es-ES') : 
                          'Nunca'}
                      </Typography>
                      {editIntegration.lastTestStatus && (
                        <Chip 
                          size="small" 
                          label={editIntegration.lastTestStatus === 'success' ? 'Exitosa' : 'Fallida'}
                          color={editIntegration.lastTestStatus === 'success' ? 'success' : 'error'}
                          sx={{ mt: 0.5 }}
                        />
                      )}
                    </Box>
                  </Stack>
                </Paper>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleCloseDialog}>
              Cancelar
            </Button>
            {editIntegration?.id && (
              <Button
                variant="outlined"
                onClick={() => {
                  handleTestIntegration(editIntegration);
                }}
                startIcon={<SettingsIcon />}
              >
                Probar Conexión
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!editIntegration?.title || customFields.length === 0}
            >
              {editIntegration?.id ? "Guardar Cambios" : "Crear Integración"}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>

      <style jsx>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spinning {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
}
